import { 
  doc, 
  getDoc, 
  getDocs, 
  setDoc, 
  updateDoc, 
  query, 
  where, 
  collection, 
  orderBy, 
  limit, 
  increment,
  onSnapshot,
  deleteDoc,
  serverTimestamp,
  addDoc
} from 'firebase/firestore';
import { db as firestore, auth } from './firebase';
import { initializeApp, getApps } from 'firebase/app';
import { getFirestore, enableIndexedDbPersistence } from 'firebase/firestore';
import firebaseConfig from '../../firebase-applet-config.json';

export interface CacheEntry<T> {
  expiry: number;
  data: T;
}

export function getCachedConfig(appId: string): any | null {
  try {
    const raw = localStorage.getItem(`config_${appId}`);
    if (!raw) return null;
    const entry: CacheEntry<any> = JSON.parse(raw);
    if (Date.now() < entry.expiry) {
      console.log(`🚀 [Cache Hit] config_${appId}`);
      return entry.data;
    }
    console.log(`⏱️ [Cache Expired] config_${appId}`);
    localStorage.removeItem(`config_${appId}`);
  } catch (e) {
    console.error('getCachedConfig error', e);
  }
  return null;
}

export function setCachedConfig(appId: string, config: any) {
  try {
    const entry: CacheEntry<any> = {
      expiry: Date.now() + 10 * 60 * 1000, // 10 minutes
      data: config
    };
    localStorage.setItem(`config_${appId}`, JSON.stringify(entry));
  } catch (e) {
    console.error('setCachedConfig error', e);
  }
}

export function getCachedTasks(appId: string): any[] | null {
  try {
    const raw = localStorage.getItem(`tasks_${appId}`);
    if (!raw) return null;
    const entry: CacheEntry<any[]> = JSON.parse(raw);
    if (Date.now() < entry.expiry) {
      console.log(`🚀 [Cache Hit] tasks_${appId}`);
      return entry.data;
    }
    console.log(`⏱️ [Cache Expired] tasks_${appId}`);
    localStorage.removeItem(`tasks_${appId}`);
  } catch (e) {
    console.error('getCachedTasks error', e);
  }
  return null;
}

export function setCachedTasks(appId: string, tasks: any[]) {
  try {
    const entry: CacheEntry<any[]> = {
      expiry: Date.now() + 5 * 60 * 1000, // 5 minutes
      data: tasks
    };
    localStorage.setItem(`tasks_${appId}`, JSON.stringify(entry));
  } catch (e) {
    console.error('setCachedTasks error', e);
  }
}

export function getExpiredCachedConfig(appId: string): any | null {
  try {
    const raw = localStorage.getItem(`config_${appId}`);
    if (!raw) return null;
    const entry = JSON.parse(raw);
    return entry.data || null;
  } catch (e) {
    return null;
  }
}

export function getExpiredCachedTasks(appId: string): any[] | null {
  try {
    const raw = localStorage.getItem(`tasks_${appId}`);
    if (!raw) return null;
    const entry = JSON.parse(raw);
    return entry.data || null;
  } catch (e) {
    return null;
  }
}

// ----------------------------------------------------
// DB REWRITE & INTERACTIVE DEBOUNCE CODE
// ----------------------------------------------------
const templateDbInstances = new Map<string, any>();
const appToTemplateNameMap = new Map<string, string>();

let activeTemplateProjectFs: any = null;
let activeTemplateId: string = '';

export function setActiveTemplateProject(fs: any, templateId: string) {
  activeTemplateProjectFs = fs;
  activeTemplateId = templateId;
  templateDbInstances.set(templateId, fs);
}

let customTemplateDbCache = new Map<string, any>();

export function getProjectFs(appId: string) {
  if (!appId) return firestore;

  // Check if there is an active custom database configuration defined on the nexus client config
  const activeConfig = (window as any).nexus?.config;
  if (activeConfig && (activeConfig.id === appId || activeConfig.appId === appId) && activeConfig.customApiKey && activeConfig.customProjectId) {
    const cacheKey = `${appId}_${activeConfig.customProjectId}`;
    if (customTemplateDbCache.has(cacheKey)) {
      return customTemplateDbCache.get(cacheKey);
    }
    try {
      const firebaseConfig = {
        apiKey: activeConfig.customApiKey,
        authDomain: activeConfig.customAuthDomain || `${activeConfig.customProjectId}.firebaseapp.com`,
        databaseURL: activeConfig.customDatabaseURL || `https://${activeConfig.customProjectId}-default-rtdb.firebaseio.com`,
        projectId: activeConfig.customProjectId,
        storageBucket: activeConfig.customStorageBucket || `${activeConfig.customProjectId}.appspot.com`,
        messagingSenderId: activeConfig.customMessagingSenderId || '',
        appId: activeConfig.customAppId || ''
      };
      const appName = `custom_runtime_db_${appId}`;
      const customApp = getApps().find(a => a.name === appName) || initializeApp(firebaseConfig, appName);
      const customFs = getFirestore(customApp);
      customTemplateDbCache.set(cacheKey, customFs);
      return customFs;
    } catch (e) {
      console.error('Failed to create custom database connection, falling back to dynamic partition:', e);
    }
  }

  // Retrieve isolated DB for the template associated with this appId
  const mappedTemplateName = appToTemplateNameMap.get(appId);
  if (mappedTemplateName) {
    const templateFs = templateDbInstances.get(mappedTemplateName);
    if (templateFs && templateFs !== firestore) {
      return templateFs;
    }
  }

  return firestore;
}

export async function initDynamicDb(appId: string) {
  if (!appId || appId === 'default') {
    return firestore;
  }
  try {
    let templateName = '';
    const tempDocRef = doc(firestore, 'templates', appId);
    const tempDocSnap = await getDoc(tempDocRef).catch(() => null);
    if (tempDocSnap && tempDocSnap.exists()) {
      templateName = tempDocSnap.data()?.template_id || tempDocSnap.data()?.template_name || '';
    }

    if (!templateName) {
      const miniAppRef = doc(firestore, 'mini_apps', appId);
      const miniAppSnap = await getDoc(miniAppRef).catch(() => null);
      if (miniAppSnap && miniAppSnap.exists()) {
        templateName = miniAppSnap.data()?.template_id || miniAppSnap.data()?.templateId || '';
      }
    }

    if (!templateName) {
      const localMap = localStorage.getItem(`template_map_${appId}`);
      if (localMap) {
        templateName = localMap;
      } else {
        templateName = 'hybrid_rewards_x';
      }
    } else {
      localStorage.setItem(`template_map_${appId}`, templateName);
    }

    // Capture the app to template association
    appToTemplateNameMap.set(appId, templateName);

    const credDocRef = doc(firestore, 'template_credentials', templateName);
    const credDocSnap = await getDoc(credDocRef).catch(() => null);
    let templateConfig = credDocSnap && credDocSnap.exists() ? credDocSnap.data() : null;

    if (!templateConfig) {
      templateConfig = {
        ...firebaseConfig,
        projectId: `tribloop-${templateName}-v1`,
        authDomain: `tribloop-${templateName}-v1.firebaseapp.com`,
        storageBucket: `tribloop-${templateName}-v1.appspot.com`,
      };
      try {
        await setDoc(credDocRef, templateConfig);
      } catch (e) {
        console.warn('Failed to self-heal template credentials:', e);
      }
    }

    const isCustomConfigured = templateConfig && 
                               templateConfig.apiKey && 
                               templateConfig.apiKey !== firebaseConfig.apiKey &&
                               !templateConfig.projectId.startsWith('tribloop-');

    if (!isCustomConfigured) {
      setActiveTemplateProject(firestore, templateName);
      return firestore;
    }

    const appName = `tribloop_${templateName}`;
    const targetApp = getApps().find(a => a.name === appName) || initializeApp(templateConfig, appName);
    const targetFs = getFirestore(targetApp);

    try {
      await enableIndexedDbPersistence(targetFs);
    } catch (err: any) {
      if (err.code === 'failed-precondition') {
        console.warn('Persistence failed due to multiple tabs open');
      } else {
        console.warn('Persistence failed:', err);
      }
    }

    setActiveTemplateProject(targetFs, templateName);
    return targetFs;
  } catch (error) {
    console.error('Failed to initialize dynamic Firebase project for app:', appId, error);
    setActiveTemplateProject(firestore, 'hybrid_rewards_x');
    return firestore;
  }
}

interface PendingUserWrite {
  balanceDelta: number;
  spinCountDelta: number;
  actionCount: number;
  timer: any | null;
}

const pendingWrites = new Map<string, PendingUserWrite>();

export function debounceWrite(appId: string, userId: string, balanceDelta: number, spinCountDelta: number = 0) {
  const key = `${appId}_${userId}`;
  let pending = pendingWrites.get(key);
  if (!pending) {
    pending = {
      balanceDelta: 0,
      spinCountDelta: 0,
      actionCount: 0,
      timer: null
    };
    pendingWrites.set(key, pending);
  }

  pending.balanceDelta += balanceDelta;
  pending.spinCountDelta += spinCountDelta;
  pending.actionCount += 1;

  if (pending.timer) {
    clearTimeout(pending.timer);
    pending.timer = null;
  }

  const flush = async () => {
    const toFlush = pendingWrites.get(key);
    if (!toFlush) return;
    pendingWrites.delete(key);

    try {
      console.log(`💾 [Debounced Flush] Writing to Firestore for ${key}: balance +${toFlush.balanceDelta}, spin_count +${toFlush.spinCountDelta}`);
      await db._flushUserStats(userId, appId, toFlush.balanceDelta, toFlush.spinCountDelta);
    } catch (e) {
      console.error(`Failed to flush debounced writes for user ${userId} in app ${appId}:`, e);
    }
  };

  if (pending.actionCount >= 5) {
    console.log(`⚡ [Debounced Buffer Full] 5 actions reached for ${key}. Flushing immediately.`);
    flush();
  } else {
    pending.timer = setTimeout(() => {
      flush();
    }, 5000);
  }
}

enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId?: string | null;
    email?: string | null;
  }
}

function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errInfo: FirestoreErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
    },
    operationType,
    path
  };
  console.error('Firestore Error: ', JSON.stringify(errInfo));

  if (path !== 'error_logs') {
    addDoc(collection(firestore, 'error_logs'), {
      app_id: path?.split('/')[1] || 'system',
      context: `firestore_${operationType}`,
      error: errInfo.error,
      timestamp: new Date().toISOString(),
      user_id: errInfo.authInfo.userId || 'guest',
      user_email: errInfo.authInfo.email || '',
      path: path || ''
    }).catch(e => console.error('Background DB log error failed:', e));
  }

  throw new Error(JSON.stringify(errInfo));
}

function sanitizeData(data: any) {
  const sanitized: any = {};
  for (const key in data) {
    if (data[key] !== undefined) {
      if (typeof data[key] === 'object' && data[key] !== null && !Array.isArray(data[key])) {
        // Handle serverTimestamp
        if (data[key].hasOwnProperty('_methodName') || data[key].hasOwnProperty('serverTimestamp')) {
           sanitized[key] = data[key];
        } else {
           sanitized[key] = sanitizeData(data[key]);
        }
      } else if (Array.isArray(data[key])) {
        sanitized[key] = data[key].map((item: any) => 
          (typeof item === 'object' && item !== null) ? sanitizeData(item) : item
        );
      } else {
        sanitized[key] = data[key];
      }
    }
  }
  return sanitized;
}

/**
 * FIREBASE FIRESTORE DATA LAYER
 */

export enum UserRole {
  USER = 'user',
  APP_ADMIN = 'app_admin',
  SUPER_ADMIN = 'super_admin'
}

export const ROOT_ADMIN_ID = '7804637246';

export const db = {
  // Profiles
  async getProfile(userId: string) {
    if (!userId) return null;
    const path = `profiles/${userId}`;
    try {
      const docRef = doc(firestore, 'profiles', userId);
      const docSnap = await getDoc(docRef);
      const data = docSnap.exists() ? docSnap.data() : null;
      
      if (data) {
        localStorage.setItem(`profile_${userId}`, JSON.stringify(data));
      }

      // Auto-assign SUPER_ADMIN if it's the root admin
      if (userId === ROOT_ADMIN_ID && data && !data.is_super_admin) {
         await this.updateProfile(userId, { role: UserRole.SUPER_ADMIN, is_super_admin: true }).catch(() => {});
         return { ...data, role: UserRole.SUPER_ADMIN, is_super_admin: true };
      }
      
      return data;
    } catch (error) {
      console.warn('getProfile error (likely offline/connection failed):', error);
      try {
        const localCached = localStorage.getItem(`profile_${userId}`);
        if (localCached) {
          return JSON.parse(localCached);
        }
      } catch (cacheErr) {}
      
      return {
        id: userId,
        role: (userId === ROOT_ADMIN_ID) ? UserRole.SUPER_ADMIN : UserRole.USER,
        first_name: 'Telegram User',
        username: 'user',
        display_name: 'Telegram User',
        balance: 0
      };
    }
  },

  async getUserRole(userId: string, appId?: string): Promise<UserRole> {
    // 1. Check if Root Admin (Telegram ID)
    const profile = await this.getProfile(userId).catch(() => null);

    // A. Check Global Lockdown (Catch any permission errors safely)
    const superAdminLockId = await this.getGlobalSetting('super_admin_id').catch(() => null);
    if (superAdminLockId && userId === superAdminLockId.toString()) {
       return UserRole.SUPER_ADMIN;
    }

    if (userId === ROOT_ADMIN_ID) {
      return UserRole.SUPER_ADMIN;
    }

    // 2. Check Profile Role (assigned by root or system)
    if (profile?.role === UserRole.SUPER_ADMIN || profile?.is_super_admin) {
      // Explicitly protect so that only the designated ROOT_ADMIN_ID has SUPER_ADMIN role
      if (userId === ROOT_ADMIN_ID) {
        return UserRole.SUPER_ADMIN;
      }
    }

    // 3. Check Authorized Admin List in Global Settings  (Catch any permission errors safely)
    const authorizedAdmins = await this.getGlobalSetting('authorized_super_admins').catch(() => null);
    if (authorizedAdmins && typeof authorizedAdmins === 'object' && authorizedAdmins[userId] && userId === ROOT_ADMIN_ID) {
      return UserRole.SUPER_ADMIN;
    }

    if (appId) {
      const app = await this.getAppConfigById(appId);
      if (app?.owner_id === userId) {
        return UserRole.APP_ADMIN;
      }
    }

    return UserRole.USER;
  },

  async updateProfile(userId: string, data: any) {
    if (!userId) return;
    const path = `profiles/${userId}`;
    try {
      const sanitized = sanitizeData(data);
      const docRef = doc(firestore, 'profiles', userId);
      
      // Bootstrap super admin ONLY for the root ID
      if (userId === ROOT_ADMIN_ID) {
         sanitized.role = UserRole.SUPER_ADMIN;
         sanitized.is_super_admin = true;
      } else {
         // Prevent non-root users from self-promoting
         delete sanitized.role;
         delete sanitized.is_super_admin;
      }

      await setDoc(docRef, { ...sanitized, updated_at: serverTimestamp() }, { merge: true });
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, path);
    }
  },

  async getAllProfiles() {
    try {
      const q = query(collection(firestore, 'profiles'), limit(300));
      const snap = await getDocs(q);
      return snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (e) {
      console.error('Failed to get all profiles', e);
      return [];
    }
  },

  // App User Stats (Stored in project-specific apps/{app_id}/users/{telegram_id})
  async getAppUser(userId: string, appId: string) {
    if (!userId || !appId) return null;
    const fs = getProjectFs(appId);
    const path = `apps/${appId}/users/${userId}`;
    try {
      const docRef = doc(fs, 'apps', appId, 'users', userId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const val = docSnap.data();
        // Incorporate pending debounce offset into the returned balance so UI updates are real-time responsive
        const pKey = `${appId}_${userId}`;
        const pending = pendingWrites.get(pKey);
        if (pending && val) {
          return {
            ...val,
            balance: (val.balance || 0) + pending.balanceDelta,
            spin_count: (val.spin_count || 0) + pending.spinCountDelta
          };
        }
        return val;
      } else {
        // Double check master DB for migration/fallback
        try {
          const masterId = `${appId}_${userId}`;
          const masterDocRef = doc(firestore, 'app_users', masterId);
          const masterDocSnap = await getDoc(masterDocRef);
          if (masterDocSnap.exists()) {
            const masterData = masterDocSnap.data();
            const isolatedDocRef = doc(fs, 'apps', appId, 'users', userId);
            await setDoc(isolatedDocRef, masterData, { merge: true });
            return masterData;
          }
        } catch (masterErr) {
          console.warn('Master migrate lookup skipped:', masterErr);
        }
        return await this.initAppUser(userId, appId);
      }
    } catch (error) {
      handleFirestoreError(error, OperationType.GET, path);
    }
  },

  async initAppUser(userId: string, appId: string) {
    if (!userId || !appId) throw new Error('Invalid user or app context');
    const fs = getProjectFs(appId);
    const path = `apps/${appId}/users/${userId}`;
    try {
      const docRef = doc(fs, 'apps', appId, 'users', userId);
      const data = {
        user_id: userId,
        app_id: appId,
        balance: 0,
        referral_count: 0,
        spin_count: 0,
        status: 'active',
        created_at: serverTimestamp()
      };
      await setDoc(docRef, data);
      return data;
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, path);
    }
  },

  async updateAppUserData(userId: string, appId: string, data: any) {
    if (!userId || !appId) return;
    const fs = getProjectFs(appId);
    try {
       const docRef = doc(fs, 'apps', appId, 'users', userId);
       await setDoc(docRef, sanitizeData(data), { merge: true });
    } catch (e) {
       console.error('Failed to update app user profile:', e);
    }
  },

  async updateBalance(userId: string, appId: string, amount: number) {
    if (!userId || !appId) return;
    debounceWrite(appId, userId, amount, 0);
  },

  async updateSpinCount(userId: string, appId: string, amount: number) {
    if (!userId || !appId) return;
    debounceWrite(appId, userId, 0, amount);
  },

  async _flushUserStats(userId: string, appId: string, balanceDelta: number, spinCountDelta: number) {
    const fs = getProjectFs(appId);
    const docRef = doc(fs, 'apps', appId, 'users', userId);
    try {
      const updates: any = {
        updated_at: serverTimestamp()
      };
      if (balanceDelta !== 0) {
        updates.balance = increment(balanceDelta);
      }
      if (spinCountDelta !== 0) {
        updates.spin_count = increment(spinCountDelta);
      }
      await setDoc(docRef, updates, { merge: true });
    } catch (error) {
      console.error('_flushUserStats failed', error);
    }
  },

  async setUserStatus(appId: string, userId: string, status: string) {
    if (!userId || !appId) return;
    const fs = getProjectFs(appId);
    const path = `apps/${appId}/users/${userId}`;
    try {
      const docRef = doc(fs, 'apps', appId, 'users', userId);
      await setDoc(docRef, { status, updated_at: serverTimestamp() }, { merge: true });
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, path);
    }
  },

  // Mini Apps
  async getAppConfig(alias: string) {
    if (!alias) return null;
    const cached = getCachedConfig(alias);
    if (cached) return cached;

    // Initialize dynamic template project FS for this alias (or appId)
    const fs = await initDynamicDb(alias);

    // Try to retrieve configuration from project-specific location: apps/{app_id}/config/default
    try {
      const docRef = doc(fs, 'apps', alias, 'config', 'default');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        const config = { ...data, id: alias };
        setCachedConfig(alias, config);
        return config;
      }
    } catch (e) {
      console.warn(`Could not read isolated config for alias ${alias}, trying master fallback:`, e);
    }

    // Fallback: Query from Master DB's mini_apps collection
    const path = 'mini_apps';
    try {
      const q = query(collection(firestore, 'mini_apps'), where('alias', '==', alias), limit(1));
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        const data = snapshot.docs[0].data();
        const config: any = { ...data, id: snapshot.docs[0].id };

        // Self-heal/populate the project-specific DB: apps/{app_id}/config/default
        try {
          const targetFs = getProjectFs(alias);
          if (targetFs !== firestore) {
            await setDoc(doc(targetFs, 'apps', alias, 'config', 'default'), config, { merge: true });
            // Register template mapping in master templates collection
            await setDoc(doc(firestore, 'templates', alias), { id: alias, template_id: config.template_id || config.templateId || 'hybrid_rewards_x' }, { merge: true });
          }
        } catch (writeErr) {
          console.warn('Failed self-healing write to template DB:', writeErr);
        }

        setCachedConfig(alias, config);
        return config;
      } else {
        // Double check direct doc ID query as secondary fallback
        const docRef = doc(firestore, 'mini_apps', alias);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const config: any = { ...docSnap.data(), id: alias };
          try {
            const targetFs = getProjectFs(alias);
            if (targetFs !== firestore) {
              await setDoc(doc(targetFs, 'apps', alias, 'config', 'default'), config, { merge: true });
              await setDoc(doc(firestore, 'templates', alias), { id: alias, template_id: config.template_id || config.templateId || 'hybrid_rewards_x' }, { merge: true });
            }
          } catch (writeErr) {
            console.warn('Failed self-healing write to template DB:', writeErr);
          }
          setCachedConfig(alias, config);
          return config;
        }
      }
      return null;
    } catch (error) {
      console.warn(`getAppConfig failed for ${alias} (likely offline):`, error);
      const fallback = getExpiredCachedConfig(alias);
      if (fallback) {
        return fallback;
      }
      handleFirestoreError(error, OperationType.GET, path);
    }
  },

  async getAppConfigById(appId: string) {
    return this.getAppConfig(appId);
  },

  // Tasks
  async getGlobalTasks() {
    const path = 'global_tasks';
    try {
      const q = query(collection(firestore, 'global_tasks'), orderBy('created_at', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      handleFirestoreError(error, OperationType.LIST, path);
    }
  },

  async createGlobalTask(data: any) {
    const path = 'global_tasks';
    try {
      const docRef = await addDoc(collection(firestore, 'global_tasks'), { 
        ...data, 
        created_at: serverTimestamp() 
      });
      await updateDoc(docRef, { id: docRef.id });
      const snap = await getDoc(docRef);
      return snap.data();
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, path);
    }
  },

  async deleteGlobalTask(id: string) {
    const path = `global_tasks/${id}`;
    try {
      await deleteDoc(doc(firestore, 'global_tasks', id));
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, path);
    }
  },

  async getTasks(appId: string) {
    if (!appId) return [];
    
    // Check local tasks cache first
    const cached = getCachedTasks(appId);
    if (cached) return cached;

    const fs = getProjectFs(appId);
    const path = `apps/${appId}/tasks`;
    try {
      const q = query(
        collection(fs, 'apps', appId, 'tasks'),
        orderBy('created_at', 'desc')
      );
      const snapshot = await getDocs(q);
      const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      setCachedTasks(appId, list);
      return list;
    } catch (error) {
      // Fallback: Query from Master DB and populate/self-heal template DB
      try {
        const fallbackQuery = query(
          collection(firestore, 'tasks'),
          where('app_id', '==', appId)
        );
        const snapshot = await getDocs(fallbackQuery);
        const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() as any }));
        
        // Safe in-memory sorting to avoid composite index requirement
        list.sort((a, b) => {
          const tA = a.created_at?.seconds || 0;
          const tB = b.created_at?.seconds || 0;
          return tB - tA;
        });

        // Self-heal: Copy to project-specific DB: apps/{app_id}/tasks/{id}
        for (const t of list) {
          await setDoc(doc(fs, 'apps', appId, 'tasks', t.id), t, { merge: true });
        }

        setCachedTasks(appId, list);
        return list;
      } catch (fallbackErr) {
        console.warn('Fallback getTasks query failed, returning cached or empty:', fallbackErr);
        const expired = getExpiredCachedTasks(appId);
        if (expired && expired.length > 0) {
          return expired;
        }
        return [];
      }
    }
  },

  async createTask(taskData: any) {
    if (!taskData?.app_id) throw new Error('App identity missing');
    const appId = taskData.app_id;
    const fs = getProjectFs(appId);
    const path = `apps/${appId}/tasks`;
    try {
      const sanitized = sanitizeData(taskData);
      const docRef = await addDoc(collection(fs, 'apps', appId, 'tasks'), { ...sanitized, created_at: serverTimestamp() });
      await updateDoc(docRef, { id: docRef.id });
      
      // Invalidate local tasks list cache
      localStorage.removeItem(`tasks_${appId}`);

      const snap = await getDoc(docRef);
      return snap.data();
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, path);
    }
  },

  async deleteTask(appId: string, taskId: string) {
    if (!taskId || !appId) return;
    const fs = getProjectFs(appId);
    const path = `apps/${appId}/tasks/${taskId}`;
    try {
      await deleteDoc(doc(fs, 'apps', appId, 'tasks', taskId));
      
      // Invalidate local tasks list cache
      localStorage.removeItem(`tasks_${appId}`);
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, path);
    }
  },

  // Submissions
  async submitTask(appId: string, taskId: string, userId: string, submissionData: { type: string, value: string, proof_url?: string, is_global?: boolean }) {
    if (!appId || !userId || !taskId) throw new Error('Incomplete submission context');
    const fs = getProjectFs(appId);
    const path = `apps/${appId}/submissions`;
    try {
      // Check for existing submissions to prevent duplicate rewards exploit
      const q = query(
        collection(fs, 'apps', appId, 'submissions'),
        where('task_id', '==', taskId),
        where('user_id', '==', userId)
      );
      const existing = await getDocs(q);
      const alreadyExists = existing.docs.some(d => d.data()?.status === 'approved' || d.data()?.status === 'pending');
      if (alreadyExists) {
        throw new Error('This mission already has a record of submission or completion and is locked.');
      }

      const docRef = await addDoc(collection(fs, 'apps', appId, 'submissions'), {
        user_id: userId,
        task_id: taskId,
        app_id: appId,
        type: submissionData.type,
        value: submissionData.value,
        proof_url: submissionData.proof_url || null,
        status: 'pending',
        is_global: !!submissionData.is_global,
        timestamp: Date.now(),
        created_at: serverTimestamp()
      });
      await updateDoc(docRef, { id: docRef.id });
      const snap = await getDoc(docRef);
      return snap.data();
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, path);
    }
  },

  async getGlobalTask(taskId: string) {
    const path = `global_tasks/${taskId}`;
    try {
      const docSnap = await getDoc(doc(firestore, 'global_tasks', taskId));
      return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
    } catch (error) {
      handleFirestoreError(error, OperationType.GET, path);
    }
  },

  async getTask(taskId: string, appId?: string) {
    const fs = appId ? getProjectFs(appId) : firestore;
    const path = appId ? `apps/${appId}/tasks/${taskId}` : `tasks/${taskId}`;
    try {
      const docSnap = await getDoc(appId ? doc(fs, 'apps', appId, 'tasks', taskId) : doc(fs, 'tasks', taskId));
      return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } : null;
    } catch (error) {
      handleFirestoreError(error, OperationType.GET, path);
    }
  },

  async getTaskSubmissions(appId: string, filterStatus?: string) {
    if (!appId) return [];
    const fs = getProjectFs(appId);
    const path = `apps/${appId}/submissions`;
    try {
      const colRef = collection(fs, 'apps', appId, 'submissions');
      let q = colRef;
      if (filterStatus) {
        q = query(colRef, where('status', '==', filterStatus)) as any;
      }
      const snapshot = await getDocs(q);
      const subs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Map profiles and tasks
      const results = await Promise.all(subs.map(async (sub: any) => {
        const [task, profile] = await Promise.all([
          sub.is_global ? this.getGlobalTask(sub.task_id) : (sub.task_id ? this.getTask(sub.task_id, appId) : Promise.resolve(null)),
          sub.user_id ? this.getProfile(sub.user_id) : Promise.resolve(null)
        ]);
        return {
          ...sub,
          tasks: task,
          profiles: profile
        };
      }));

      return results;
    } catch (error) {
      console.warn('Failed to retrieve task submissions, returning empty:', error);
      return [];
    }
  },

  async approveSubmission(submissionId: string, userId: string, appId: string, reward: number) {
    const fs = getProjectFs(appId);
    const path = `apps/${appId}/submissions/${submissionId}`;
    try {
      const docRef = doc(fs, 'apps', appId, 'submissions', submissionId);
      await updateDoc(docRef, { status: 'approved' });
      
      // Update balance if there's a reward
      if (reward > 0) {
        await this.updateBalance(userId, appId, reward);
      }
      
      const statsRef = doc(fs, 'apps', appId, 'users', userId);
      const statsSnap = await getDoc(statsRef);
      if (statsSnap.exists()) {
        await setDoc(statsRef, { tasks_completed_count: increment(1) }, { merge: true });
      } else {
        await this.initAppUser(userId, appId);
        await setDoc(statsRef, { tasks_completed_count: increment(1) }, { merge: true });
      }
      return true;
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, path);
    }
  },

  async declineSubmission(appId: string, submissionId: string, feedback: string) {
    const fs = getProjectFs(appId);
    const path = `apps/${appId}/submissions/${submissionId}`;
    try {
      const docRef = doc(fs, 'apps', appId, 'submissions', submissionId);
      await updateDoc(docRef, { status: 'declined', admin_feedback: feedback });
      return true;
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, path);
    }
  },

  // Management
  async checkWithdrawalEligibility(userId: string, appId: string) {
    try {
      const config = await this.getAppConfigById(appId);
      const globalTasks = (await this.getGlobalTasks()) || [];
      const mandatoryGlobalTasks = globalTasks.filter((t: any) => t.is_mandatory);
      
      if (mandatoryGlobalTasks.length === 0) return { eligible: true };

      const fs = getProjectFs(appId);
      // Check completions in the correct project subcollection
      const q = query(
        collection(fs, 'apps', appId, 'submissions'),
        where('user_id', '==', userId),
        where('status', '==', 'approved'),
        where('is_global', '==', true)
      );
      const snapshot = await getDocs(q);
      const completedTaskIds = new Set(snapshot.docs.map(doc => doc.data().task_id));

      const missing = mandatoryGlobalTasks.filter((t: any) => !completedTaskIds.has(t.id));
      
      if (missing.length > 0) {
        return { 
          eligible: false, 
          reason: 'Global Verification Required', 
          missingTasks: missing.map((t: any) => t.title)
        };
      }
      return { eligible: true };
    } catch (error) {
      console.error('Eligibility check failed:', error);
      return { eligible: true }; // Fallback to safe
    }
  },

  async getOwnedApps(ownerId: string) {
    const path = 'mini_apps';
    try {
      const q = query(collection(firestore, 'mini_apps'), where('owner_id', '==', ownerId));
      const snapshot = await getDocs(q);
      const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() as any }));
      // Safe in-memory sorting to avoid composite index requirements
      return list.sort((a, b) => {
         const tA = a.created_at?.seconds || 0;
         const tB = b.created_at?.seconds || 0;
         return tB - tA;
      });
    } catch (error) {
      handleFirestoreError(error, OperationType.LIST, path);
    }
  },

  async getAllApps() {
    const path = 'mini_apps';
    try {
      const q = query(collection(firestore, 'mini_apps'), orderBy('created_at', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
       handleFirestoreError(error, OperationType.LIST, path);
    }
  },

  async getAllWithdrawals() {
     const path = 'withdrawals';
     try {
        const q = query(collection(firestore, 'withdrawals'), orderBy('created_at', 'desc'), limit(50));
        const snapshot = await getDocs(q);
        const results = await Promise.all(snapshot.docs.map(async (d) => {
           const data = d.data();
           const [profile, app] = await Promise.all([
              this.getProfile(data.user_id),
              this.getAppConfigById(data.app_id)
           ]);
           return { id: d.id, ...data, profiles: profile, app };
        }));
        return results;
     } catch (e) { return []; }
  },

  async getPublicApps() {
    const path = 'mini_apps';
    try {
      const q = query(collection(firestore, 'mini_apps'), orderBy('created_at', 'desc'), limit(20));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => doc.data());
    } catch (error) {
      handleFirestoreError(error, OperationType.LIST, path);
    }
  },

  // Withdrawals
  async getWithdrawals(appId: string) {
    if (!appId) return [];
    const fs = getProjectFs(appId);
    const path = `apps/${appId}/withdrawals`;
    try {
      const q = query(collection(fs, 'apps', appId, 'withdrawals'), orderBy('created_at', 'desc'));
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.warn('Failed to retrieve withdrawals, returning empty:', error);
      return [];
    }
  },

  async updateWithdrawalStatus(appId: string, withdrawalId: string, status: string, proofUrl?: string) {
    const fs = getProjectFs(appId);
    const path = `apps/${appId}/withdrawals/${withdrawalId}`;
    try {
      const docRef = doc(fs, 'apps', appId, 'withdrawals', withdrawalId);
      const snap = await getDoc(docRef);
      
      if (!snap.exists()) throw new Error('Withdrawal not found');
      const withdrawal = snap.data();
      const previousStatus = withdrawal.status;

      // Handle refund if rejecting or declining
      const isDeclined = status === 'rejected' || status === 'declined' || status === 'failed';
      const needsRefund = isDeclined && previousStatus !== 'rejected' && previousStatus !== 'declined' && previousStatus !== 'completed';
      
      if (needsRefund) {
        await this.updateBalance(withdrawal.user_id, appId, withdrawal.amount);
      }
      
      await updateDoc(docRef, { 
        status, 
        proof_url: proofUrl || null,
        updated_at: serverTimestamp() 
      });
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, path);
    }
  },

  async getAppUsers(appId: string) {
    if (!appId) return [];
    const fs = getProjectFs(appId);
    const path = `apps/${appId}/users`;
    try {
      const colRef = collection(fs, 'apps', appId, 'users');
      const snapshot = await getDocs(colRef);
      const users = snapshot.docs.map(doc => doc.data());
      
      const usersWithProfiles = await Promise.all(users.map(async (u: any) => {
        const profile = await this.getProfile(u.user_id);
        return { ...u, profiles: profile };
      }));
      return usersWithProfiles;
    } catch (error) {
      // Fallback: Check master DB
      try {
        const q = query(collection(firestore, 'app_users'), where('app_id', '==', appId));
        const snapshot = await getDocs(q);
        const users = snapshot.docs.map(doc => doc.data());
        const usersWithProfiles = await Promise.all(users.map(async (u: any) => {
          const profile = await this.getProfile(u.user_id);
          return { ...u, profiles: profile };
        }));
        return usersWithProfiles;
      } catch (fallbackErr) {
        handleFirestoreError(error, OperationType.LIST, path);
      }
    }
  },

  async getGlobalSetting(key: string) {
    const path = `global_settings/${key}`;
    try {
      const docRef = doc(firestore, 'global_settings', key);
      const docSnap = await getDoc(docRef);
      const val = docSnap.exists() ? docSnap.data()?.value : null;
      if (val !== undefined && val !== null) {
        localStorage.setItem(`global_setting_${key}`, JSON.stringify(val));
      }
      return val;
    } catch (error) {
      console.warn(`getGlobalSetting failed for ${key} (likely offline):`, error);
      try {
        const local = localStorage.getItem(`global_setting_${key}`);
        if (local) return JSON.parse(local);
      } catch (e) {}
      return null;
    }
  },

  async setGlobalSetting(key: string, value: any) {
    const path = `global_settings/${key}`;
    try {
      const docRef = doc(firestore, 'global_settings', key);
      await setDoc(docRef, { value, updated_at: serverTimestamp() }, { merge: true });
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, path);
    }
  },

  async getGlobalLeaderboard() {
     const path = 'mini_apps';
     try {
       const snapshot = await getDocs(collection(firestore, 'mini_apps'));
       const apps = snapshot.docs.map(doc => doc.data());
       const counts: any = {};
       apps.forEach((a: any) => {
         counts[a.owner_id] = (counts[a.owner_id] || 0) + 1;
       });
       const result = await Promise.all(Object.entries(counts).map(async ([uid, count]) => {
         const profile = await this.getProfile(uid);
         return { profile, count };
       }));
       return result.sort((a:any, b:any) => b.count - a.count).slice(0, 5);
     } catch (e) { return []; }
  },

  async createApp(appData: any) {
    const path = 'mini_apps';
    try {
      const sanitized = sanitizeData(appData);
      const docRef = await addDoc(collection(firestore, 'mini_apps'), { ...sanitized, created_at: serverTimestamp() });
      await updateDoc(docRef, { id: docRef.id });
      const config = { ...sanitized, id: docRef.id };

      // Propagate registry mapping to templates collection in Master DB so initDynamicDb can locate it
      const templateIdStr = appData.template_id || appData.templateId || 'hybrid_rewards_x';
      await setDoc(doc(firestore, 'templates', docRef.id), { id: docRef.id, template_id: templateIdStr }, { merge: true });

      // Self-heal/clone the config to isolated database directly on creation
      try {
        const targetFs = await initDynamicDb(docRef.id);
        if (targetFs !== firestore) {
          await setDoc(doc(targetFs, 'apps', docRef.id, 'config', 'default'), config, { merge: true });
        }
      } catch (creationFsErr) {
        console.warn('Failed to propagate new app config to partitioned DB:', creationFsErr);
      }

      setCachedConfig(docRef.id, config);
      return config;
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, path);
    }
  },

  async updateApp(appId: string, updates: any) {
    if (!appId) return;
    const path = `mini_apps/${appId}`;
    try {
      const sanitized = sanitizeData(updates);
      
      // Update Master DB config (using setDoc with merge: true for self-healing/safety)
      const docRef = doc(firestore, 'mini_apps', appId);
      await setDoc(docRef, sanitized, { merge: true });

      // Ensure templates registry mapping is stored
      if (updates.template_id || updates.templateId) {
        const templateIdStr = updates.template_id || updates.templateId;
        await setDoc(doc(firestore, 'templates', appId), { id: appId, template_id: templateIdStr }, { merge: true }).catch(() => null);
      }

      // Update isolated DB config
      const fs = getProjectFs(appId);
      if (fs !== firestore) {
        await setDoc(doc(fs, 'apps', appId, 'config', 'default'), sanitized, { merge: true });
      }

      // Cache invalidation
      localStorage.removeItem(`config_${appId}`);
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, path);
    }
  },

  async deleteApp(appId: string) {
    if (!appId) return;
    const path = `mini_apps/${appId}`;
    try {
      await deleteDoc(doc(firestore, 'mini_apps', appId));
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, path);
    }
  },

  async checkTelegramMembership(userId: string, channelUsername: string, botToken?: string) {
    // 1. Check if user is already verified in our DB record (populated by bot or manual approval)
    try {
      const profile = await this.getProfile(userId);
      const verifiedChannels = profile?.verified_channels || [];
      
      const isAlreadyVerified = verifiedChannels.some((ch: string) => 
        ch.toLowerCase().replace('@', '') === channelUsername.toLowerCase().replace('@', '') ||
        channelUsername.toLowerCase().includes(ch.toLowerCase().replace('@', ''))
      );

      if (isAlreadyVerified) {
        return { joined: true, status: 'verified_via_db' };
      }

      // 2. Client-side fetch using bot token if provided (Smart Verification)
      if (botToken) {
         try {
            // Clean username for API
            const chatId = channelUsername.startsWith('@') ? channelUsername : `@${channelUsername}`;
            const response = await fetch(`https://api.telegram.org/bot${botToken}/getChatMember?chat_id=${chatId}&user_id=${userId}`);
            const data = await response.json();
            
            if (data.ok) {
               const status = data.result.status;
               const isJoined = ['creator', 'administrator', 'member'].includes(status);
               
               if (isJoined) {
                  // Auto-update DB for future speed? Optional, but helpful.
                  return { joined: true, status: 'verified_via_live_api' };
               }
            }
         } catch (apiError) {
            console.warn('Telegram API call failed, falling back to manual check');
         }
      }

      // 3. Fallback for Super Admin
      if (profile?.is_super_admin) return { joined: true, status: 'admin_bypass' };

      return { joined: false, error: 'Membership not confirmed. Please ensure you have joined the channel.' };
    } catch (e) {
      return { joined: false, error: 'Synchronization failed. Please try again later.' };
    }
  },

  // Withdrawal Requests
  async requestWithdrawal(appId: string, userId: string, data: { amount: number, method: string, details: any }) {
    if (!appId || !userId) throw new Error('Identity verify failed');
    const fs = getProjectFs(appId);
    const path = `apps/${appId}/withdrawals`;
    try {
       const docRef = await addDoc(collection(fs, 'apps', appId, 'withdrawals'), {
          ...sanitizeData(data),
          app_id: appId,
          user_id: userId,
          status: 'pending',
          created_at: serverTimestamp()
       });
       await updateDoc(docRef, { id: docRef.id });
       return true;
    } catch (error) {
       handleFirestoreError(error, OperationType.CREATE, path);
    }
  },

  // Notifications for Creators
  async getCreatorNotifications(userId: string) {
     const path = 'notifications';
     try {
        const q = query(
           collection(firestore, 'notifications'), 
           where('target_user_id', 'in', [userId, 'all'])
        );
        const snapshot = await getDocs(q);
        const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() as any }));
        // Safe in-memory sorting to avoid composite index requirements
        list.sort((a, b) => {
           const tA = a.created_at?.seconds || a.created_at || 0;
           const tB = b.created_at?.seconds || b.created_at || 0;
           return tB - tA;
        });
        return list.slice(0, 20);
     } catch (e) { return []; }
  },

  async createNotification(data: { title: string, message: string, priority: string, target_user_id: string }) {
    if (!data.target_user_id) throw new Error('Target missing');
    const path = 'notifications';
    try {
      const docRef = await addDoc(collection(firestore, 'notifications'), {
        ...data,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp()
      });
      await updateDoc(docRef, { id: docRef.id });
      return true;
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, path);
    }
  },

  async saveTemplateCredentials(templateId: string, credentials: any) {
    const credDocRef = doc(firestore, 'template_credentials', templateId);
    await setDoc(credDocRef, credentials, { merge: true });
  },

  async getTemplateCredentials(templateId: string) {
    const credDocRef = doc(firestore, 'template_credentials', templateId);
    const snap = await getDoc(credDocRef);
    if (snap.exists()) {
      return snap.data();
    }
    return null;
  },

  async recordReferral(appId: string, inviterId: string, referralId: string, reward: number) {
    const fs = getProjectFs(appId);
    const path = `apps/${appId}/referrals`;
    try {
       await addDoc(collection(fs, 'apps', appId, 'referrals'), {
          app_id: appId,
          inviter_id: inviterId,
          referral_id: referralId,
          reward,
          created_at: serverTimestamp()
       });
       
       // Update inviter balance and stats within isolated DB
       const inviterRef = doc(fs, 'apps', appId, 'users', inviterId);
       const inviterSnap = await getDoc(inviterRef);
       
       if (inviterSnap.exists()) {
          await setDoc(inviterRef, {
             balance: increment(reward),
             referral_count: increment(1),
             updated_at: serverTimestamp()
          }, { merge: true });
       } else {
          await this.initAppUser(inviterId, appId);
          await setDoc(inviterRef, {
             balance: increment(reward),
             referral_count: increment(1)
          }, { merge: true });
       }
       return true;
    } catch (error) {
       handleFirestoreError(error, OperationType.CREATE, path);
    }
  },

  async logSystemError(appId: string, context: string, errorMsg: string, details?: any) {
    try {
      await addDoc(collection(firestore, 'error_logs'), {
        app_id: appId || 'system',
        context: context || 'general',
        error: errorMsg,
        timestamp: new Date().toISOString(),
        user_id: auth.currentUser?.uid || 'guest',
        user_email: auth.currentUser?.email || '',
        ...details
      });
    } catch (e) {
      console.error('Failed to write to error_logs collection:', e);
    }
  },

  async getSystemErrors() {
    try {
      const q = query(collection(firestore, 'error_logs'), orderBy('timestamp', 'desc'), limit(100));
      const snap = await getDocs(q);
      return snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error fetching system logs', error);
      return [];
    }
  },

  async clearSystemErrors() {
    try {
      const snap = await getDocs(collection(firestore, 'error_logs'));
      const batchPromises = snap.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(batchPromises);
      return true;
    } catch (error) {
      console.error('Failed to clear logs', error);
      return false;
    }
  }
};
